export const FeatureThingTypeMrn = "mrn:sl2:::feature";

export enum FeatureEnum {
  ReservationExpirationExtension = "ReservationExpirationExtension",
  /**
   * LOCKER-324 — automatic pickup reminder. Gates `fnCheckReservationReminders`
   * per tenant: the scheduled scan only ever considers tenants that have this
   * activated (an activated `FeatureAssignment`, or `defaultActivated` on the
   * `Feature` thing), so the scan is inert on deploy and a tenant is opted in
   * from the SA console rather than by a template change.
   *
   * ⚠ **ACTIVATION IS A TYPED STRING, AND A TYPO OPTS THE TENANT OUT SILENTLY**
   * (LOCKER-324 review F1.4). `FeatureForm` takes `refName` as free text
   * (`featureValidators` only checks letters-and-length), and
   * `canTenantUseFeature` compares `refName === feature` with no error path — so
   * a `Feature` created as `AutomaticPickupReminders` or
   * `automaticPickupReminder` validates, saves and assigns cleanly, and the
   * tenant then never receives a reminder with nothing logged anywhere. The
   * pre-existing `ReservationExpirationExtension` has the same exposure.
   *
   * To turn the feature on for a tenant, in the SA console:
   *   1. create the `Feature` with `refName` **exactly** `AutomaticPickupReminder`
   *      — case-sensitive, no trailing `s`, no whitespace;
   *   2. leave `defaultActivated` OFF (on = every tenant, which is not what
   *      "activable per tenant" means);
   *   3. assign it to the tenant with `isActivated: true`;
   *   4. set that tenant's `configReminderPeriodDays` on the Configuration page
   *      (absent falls back to `DEFAULT_REMINDER_PERIOD_DAYS` = 4).
   * Confirm from the `RemindersTenantsActivated` metric, or the
   * `Automatic pickup reminder scan complete` log line's `tenantsActivated`.
   *
   * A tenant with this on is still in observe-only mode until
   * `AutomaticPickupReminderLive` is also on — see below.
   */
  AutomaticPickupReminder = "AutomaticPickupReminder",
  /**
   * LOCKER-324 — the second switch: does an opted-in tenant's scan actually
   * SEND, or only count what it would have sent?
   *
   * Off (the default, since an unassigned feature is off) means dry run: the
   * scan reads the tenant, applies every guard, selects the due reservations,
   * emits `RemindersWouldCreate` — and messages nobody. On means live.
   *
   * **Phrased positively so it fails closed.** "Live" absent is the safe state,
   * which is the same default the retired `configReminderDryRun` had. What
   * changed is where the switch lives: this is a `Feature`, so it is SA-only and
   * invisible on the tenant's own Configuration page, and it inherits the
   * `FeatureTenantsList` batch controls — activate or deactivate one tenant, the
   * filtered set, or every tenant, in one write and with no deploy. That last
   * one is the incident lever this switch exists for.
   *
   * The two features are deliberately separate rather than one tri-state:
   * revoking `Live` during an incident must not lose which tenants were opted
   * in, so that turning sending back on is one action rather than a
   * reconstruction.
   *
   * ⚠ Same typed-string exposure as above, and here the typo direction is the
   * safe one — a mistyped `refName` reads as "not live", so the tenant sits in
   * dry run rather than sending unexpectedly. The signal is
   * `RemindersTenantsDryRun` staying pinned to `RemindersTenantsActivated`.
   */
  AutomaticPickupReminderLive = "AutomaticPickupReminderLive",
  /**
   * The "create a client" control in the embedded reservation flow, which
   * writes through the legacy webapp's `/client/submitWithResponse`.
   *
   * LOCKER-410 `DoD4`: this replaces a per-browser localStorage flag —
   * per-device rather than per-tenant, invisible to anyone but whoever set it,
   * and impossible to turn off for a customer.
   * The gate is UI-only by decision `N2`: the write never
   * reaches an SL2 Lambda, so `assertTenantHasFeature` has nothing to guard,
   * and the endpoint is already reachable by any `ROLE_CLERK`.
   *
   * The value must satisfy the Feature `refName` rule (`^[A-Za-z][A-Za-z0-9]*$`)
   * so a Super Admin can create the matching Feature thing from
   * `SaFeaturesPage` with no code change.
   */
  CreateMblsClient = "CreateMblsClient",
  /**
   * The three locker-occupancy charts on the KPIs tab (UI-SL2W-091) and the
   * `ReservationLockerOccupancy` report behind them.
   *
   * LOCKER-454: gated API + UI (decision `N1`) — `handleReport` refuses the
   * report with a 403 when the tenant does not resolve to granted, so hiding
   * the charts is not the only thing standing between a tenant and the data.
   * `defaultActivated: false` (decision `N2`): occupancy stays internal until
   * its v4 caveats (LOCKER-422) are resolved and a FeatureAssignment turns it
   * on per tenant.
   */
  LockerOccupancyReport = "LockerOccupancyReport",
  /**
   * LOCKER-452: `POST /report-data` answers with an S3 envelope
   * (`{url, key, expiresIn}`) instead of the body inline, and file exports
   * cap at 20k rows read in batches, with a truncation notice.
   *
   * Per tenant because the S3 path depends on a `/sl2-report-data/*`
   * CloudFront behaviour on every host serving that tenant's WFE (N2) —
   * enable a tenant only once its host routes the prefix. Off, the endpoint
   * behaves exactly as before LOCKER-452 (inline, base64 files, 10k cap), and
   * the WFE accepts either shape, so a flip needs no redeploy.
   */
  ReportDataViaS3 = "ReportDataViaS3",
  /**
   * LOCKER-522: Zebra Browser Print of the reception code — the door-drawer
   * print button, auto-print on deposit and the Printing configuration tab.
   *
   * UI-only gate: printing goes from the browser to the Browser Print agent on
   * the clerk's own machine and never reaches an SL2 Lambda. Off by default so
   * tenants without a Zebra never see a print button warning about a printer
   * they do not have.
   */
  DepositCodePrinting = "DepositCodePrinting",
}
